/// <amd-module name="VSSUI/Components/Tree/Tree" />
import { IColumn } from "OfficeFabric/DetailsList";
import { BaseComponent } from "OfficeFabric/Utilities";
import * as React from "react";
import { ITree, ITreeItemProvider, ITreeList, ITreeListItem, ITreeProps } from "../../Components/Tree/Tree.Props";
import { TreeList } from "../../Components/Tree/TreeList";

/** @deprecated */
export interface ITreeState<T> {
    treeListItems: ITreeListItem<T>[];
    treeListItemColumns: IColumn[];
}

/** @deprecated Please use import { Tree } from "VSSUI/TreeEx"*/
export class Tree<T> extends BaseComponent<ITreeProps<T>, ITreeState<T>> implements ITree<T> {
    private treeList: ITreeList<T>;
    private childrenMap: { [id: string]: ITreeListItem<T>[] }; // flat list of all children (recursive) for item - including the item itself

    constructor(props: ITreeProps<T>) {
        super(props);

        this.childrenMap = {};

        const columns = [...this.props.columns];
        const treeListItemColumns: IColumn[] = this.buildColumns(columns);

        this.state = {
            treeListItems: [],
            treeListItemColumns: treeListItemColumns
        };

        // build the tree from the root
        this.buildTree(props.treeItemProvider, undefined);
    }

    public setSelectedItemsById(selectedItemIds: string[] | undefined): void {
        if (this.treeList) {
            this.treeList.setSelectedItemsById(selectedItemIds);
        }
    }

    public componentWillReceiveProps(newProps: ITreeProps<T>) {
        if (this.props.columns !== newProps.columns) {
            const columns = [...newProps.columns];
            const treeListItemColumns: IColumn[] = this.buildColumns(columns);

            this.setState({
                treeListItemColumns: treeListItemColumns
            });
        }

        if (this.props.treeItemProvider !== newProps.treeItemProvider) {
            // If object instance is changed, we assume it's new set of items, so we re-build them
            this.childrenMap = {};
            this.buildTree(newProps.treeItemProvider, undefined);
        }
    }

    public render(): JSX.Element {
        const { treeListItems, treeListItemColumns } = this.state;

        return (
            <TreeList
                {...this.props}
                componentRef={treeList => (this.treeList = treeList)}
                treeListItems={treeListItems}
                columns={treeListItemColumns}
                onItemToggled={this.onItemToggled}
            />
        );
    }

    private buildColumns(columns: IColumn[]): IColumn[] {
        return columns.map((column: IColumn) => {
            return {
                ...column,
                onRender: (treeListItem: ITreeListItem<T>) => {
                    return column.onRender(treeListItem.treeItem);
                }
            };
        });
    }

    private buildTree(treeItemProvider: ITreeItemProvider<T>, item: T) {
        const promise = Promise.resolve(treeItemProvider.getChildren(item));

        // this will only be undefined if we are re-building the entire tree
        const treeListItem = item !== undefined && treeItemProvider.getTreeListItem(item);

        if (treeListItem) {
            treeListItem.isLoading = true;
        }

        promise.then(children => {
            // Once we have the children, we need to make the recursive call to addTreeItems
            // to aggregate all of the children.
            const promises: Promise<ITreeListItem<T>[]>[] = [];
            const indentLevel = item !== undefined ? treeListItem.indentLevel + 1 : 0;

            for (let item of children) {
                promises.push(this.addTreeItems(treeItemProvider, item, indentLevel));
            }

            // By using all, we're in an all or nothing situation, where if a single promise
            // is rejected, we won't show any nodes for the sub-tree.
            Promise.all(promises).then(results => {
                const childTreeListItems = [].concat.apply([], results as any);
                let newTreeListItems: ITreeListItem<T>[] = [];
                if (item === undefined) {
                    newTreeListItems = childTreeListItems;
                } else {
                    // If this was a sub-tree, copy over the old tree items, inserting the
                    // children of this item in the correct spot.
                    const oldTreeListItems = this.state.treeListItems;

                    for (let oldItem of oldTreeListItems) {
                        newTreeListItems.push(oldItem);
                        if (item === oldItem.treeItem.item) {
                            newTreeListItems = newTreeListItems.concat(childTreeListItems);
                        }
                    }
                }

                // If the item is no longer expanded or no longer loading, then something has
                // cancelled the expand operation, so we should no longer call setState.
                if (!treeListItem || (treeListItem && treeListItem.isExpanded && treeListItem.isLoading)) {
                    if (treeListItem) {
                        treeListItem.isLoading = false;
                    }

                    this.setState({
                        treeListItems: newTreeListItems
                    });
                }
            });
        });
    }

    private addTreeItems(treeItemProvider: ITreeItemProvider<T>, item: T, indentLevel: number): Promise<ITreeListItem<T>[]> {
        const treeListItems: ITreeListItem<T>[] = [];
        const treeListItem = this.getOrCreateTreeListItem(treeItemProvider, item, indentLevel);
        treeListItems.push(treeListItem);

        if (treeListItem.isExpanded) {
            // If the item is expanded, we have to add its children. We'll first try to get the children
            // from our cache - if we don't have them, we'll call getChildren on the item provider
            const cachedChildren = this.childrenMap[treeListItem.treeItem.id];
            if (cachedChildren !== undefined) {
                treeListItem.isLoading = false;
                return Promise.resolve(cachedChildren);
            }
            return Promise.resolve(treeItemProvider.getChildren(item)).then(children => {
                const promises: Promise<ITreeListItem<T>[]>[] = [];
                for (let item of children) {
                    promises.push(this.addTreeItems(treeItemProvider, item, indentLevel + 1));
                }
                return Promise.all(promises).then(results => {
                    const allChildren = [].concat.apply(treeListItems, results as any);
                    this.childrenMap[treeListItem.treeItem.id] = allChildren;
                    return allChildren;
                });
            });
        } else {
            return Promise.resolve(treeListItems);
        }
    }

    private getOrCreateTreeListItem(treeItemProvider: ITreeItemProvider<T>, item: T, indentLevel: number): ITreeListItem<T> {
        let treeListItem = treeItemProvider.getTreeListItem(item);

        if (!treeListItem) {
            const treeItem = treeItemProvider.getTreeItem(item);
            treeListItem = {
                treeItem: treeItem,
                indentLevel: indentLevel,
                isExpandable: treeItem.isExpandable,
                isExpanded: !!treeItem.defaultIsExpanded,
                isLoading: false
            };

            treeItemProvider.setTreeListItem(item, treeListItem);
        }

        return treeListItem;
    }

    private onItemToggled = (treeListItem: ITreeListItem<T>) => {
        if (treeListItem.isExpandable) {
            treeListItem.isExpanded = !treeListItem.isExpanded;
            const { treeItemProvider } = this.props;

            if (treeListItem.isExpanded) {
                // If the item is now expanded, we need to re-build this part of the sub-tree
                this.buildTree(treeItemProvider, treeListItem.treeItem.item);

                // Call forceUpdate to account for a new isLoading state for one of the nodes.
                this.forceUpdate();
            } else {
                // If the the item is now collapse, we need to remove all of the children of
                // this item from the tree.
                const oldTreeListItems = this.state.treeListItems;
                const newTreeListItems = [];
                let indentLevel: number | undefined = undefined;

                // Loop through all of the items currently in the tree and add these items,
                // until we hit the item that is being collapsed.
                // Then, add this item, and do not add another item until we find an item with
                // the same indent level as the item that is being collapsed.
                for (let item of oldTreeListItems) {
                    if (item === treeListItem) {
                        indentLevel = treeListItem.indentLevel;
                        newTreeListItems.push(item);
                    } else if (indentLevel === undefined || indentLevel >= item.indentLevel) {
                        newTreeListItems.push(item);
                        indentLevel = undefined;
                    }
                }

                treeListItem.isLoading = false;

                this.setState({
                    treeListItems: newTreeListItems
                });
            }
        }
    };
}
